import { PageHero } from "../components/PageHero";
import { Manifesto } from "../components/home/Manifesto";
import { Reveal } from "../components/Reveal";
import { Cpu, ShieldCheck, Crosshair, Layers } from "lucide-react";

const CAPABILITIES = [
  { icon: Cpu, title: "Advanced Materials", body: "Proprietary nTHERM composites engineered in-house for weight, heat, and impact performance." },
  { icon: Crosshair, title: "Precision Manufacturing", body: "Micron-tolerance production across ammunition and protection systems." },
  { icon: ShieldCheck, title: "Ballistic Protection", body: "Occupant and vehicle survivability solutions validated to defense standards." },
  { icon: Layers, title: "Systems Integration", body: "Multi-brand engineering aligned under one rigorous standard of quality." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About · Mission"
        title={["Defining the", "Operator's Edge."]}
        subtitle="At Ascendance International, we don't build for the 'industry standard' — we build to fix it. We are an operational-led engineering firm dedicated to eliminating the design flaws that compromise modern defense systems. By integrating advanced materials with real-world tactical experience, we design and manufacture the suppressors, handguards, and protection systems that others called impossible. Through our focused product brands, we deliver gear that isn't just mission-ready — it's mission-corrected."
      />

      {/* Story + image */}
      <section className="bg-ink py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6">
            <p className="overline mb-6">Our Story</p>
            <div className="space-y-6 text-textmuted text-base md:text-lg leading-relaxed">
              <p>
                Founded on a single conviction — that superior outcomes in the field begin
                with superior engineering — Ascendance International builds from the material
                up. We do not accept legacy limitations.
              </p>
              <p>
                Our teams operate at the intersection of materials science, precision
                manufacturing, and battlefield insight. The result is a portfolio of brands
                trusted by procurement decision-makers, distributors, and partners worldwide.
              </p>
              <p className="text-textmain">
                Innovation. Precision. Dedication. These are not slogans — they are the
                operating system of everything we ship.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2} className="lg:col-span-6">
            <div className="relative aspect-square overflow-hidden border border-white/10 bg-black" data-testid="about-brand-image">
              <img
                src={process.env.PUBLIC_URL + "/brand-composite.png?v=2"}
                alt="Ascendance International, DH Armament, and OPS brand logos"
                loading="lazy"
                className="w-full h-full object-contain"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-surface py-20 md:py-28">
        <div className="container-x">
          <Reveal className="mb-16 max-w-2xl">
            <p className="overline mb-5">Capabilities</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-textmain">
              What we do best
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="group h-full bg-panel border border-white/5 hover:border-steel/50 p-8 transition-colors duration-500">
                  <c.icon className="text-steel mb-6" size={32} strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-semibold uppercase tracking-wide text-textmain mb-3">
                    {c.title}
                  </h3>
                  <p className="text-sm text-textmuted leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Manifesto />
    </div>
  );
}
